import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Star, Ellipsis, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter
} from '@/components/ui/dialog';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ActionSheet, Chip, ChipRow } from '@/components/mobile';
import {
	addTemplateFolder,
	renameTemplateFolder,
	removeTemplateFolder
} from '@/actions/templateFolders';

const FolderRow = ({ active, count, label, icon: Icon, menu, onSelect }) => (
	<div className="group/row relative">
		<button
			type="button"
			onClick={onSelect}
			aria-current={active ? 'true' : undefined}
			className={cn(
				'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
				// touch screens can't hover, so the menu keeps its own slot
				menu && 'touch:pr-9',
				active
					? 'bg-accent font-medium text-accent-foreground'
					: 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
			)}
		>
			{Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
			<span className="min-w-0 flex-1 truncate text-left">{label}</span>
			<span
				className={cn(
					'shrink-0 text-xs tabular-nums opacity-60',
					// the menu takes this slot on hover so no row reserves
					// permanent space for it
					menu &&
						'group-hover/row:opacity-0 group-focus-within/row:opacity-0'
				)}
			>
				{count}
			</span>
		</button>
		{menu && (
			<div className="absolute right-1 top-1/2 -translate-y-1/2">
				{menu}
			</div>
		)}
	</div>
);

// Folder filtering for the template picker: a vertical rail on tablet and up,
// a row of chips on phones. Filter keys:
// 'all' | 'favorites' | 'unfiled' | a folder _id.
const FolderFilter = ({
	folders,
	counts,
	activeFilter,
	onSelect,
	canManage,
	isEnterpriseRequested,
	// the picker dialog on a short screen (a phone held sideways) swaps the
	// rail for the phone chips so the template list keeps its height
	chipsOnShortScreens = false,
	// medium widths (768-1279px) too, where the surface is narrow (Settings)
	chipsBelowWide = false,
	// phones: chips before the folders in the same row (the picker's sort)
	leadingChips = null
}) => {
	const dispatch = useDispatch();
	const templateFolders = useSelector((state) => state.templateFolders);

	const [folderDialog, setFolderDialog] = React.useState(null); // {mode: 'create'|'rename', folder?}
	const [folderTitle, setFolderTitle] = React.useState('');
	const [dialogError, setDialogError] = React.useState('');
	const [deletingFolder, setDeletingFolder] = React.useState(null);
	const [folderActionsOpen, setFolderActionsOpen] = React.useState(false);

	const closeFolderDialog = () => {
		setFolderDialog(null);
		setFolderTitle('');
		setDialogError('');
	};

	const openCreateDialog = () => {
		setFolderDialog({ mode: 'create' });
		setFolderTitle('');
		setDialogError('');
	};

	const openRenameDialog = (folder) => {
		setFolderDialog({ mode: 'rename', folder });
		setFolderTitle(folder.title || '');
		setDialogError('');
	};

	const submitFolderDialog = async () => {
		const title = folderTitle.trim();
		if (!title) {
			setDialogError('Please enter a folder name');
			return;
		}

		const duplicate = templateFolders.find(
			(folder) =>
				folder.title.toLowerCase() === title.toLowerCase() &&
				folder._id !== folderDialog?.folder?._id
		);
		if (duplicate) {
			setDialogError(`A folder named "${title}" already exists`);
			return;
		}

		const result =
			folderDialog.mode === 'rename'
				? await dispatch(
						renameTemplateFolder({
							_folder: folderDialog.folder._id,
							title
						})
					)
				: await dispatch(addTemplateFolder({ title }));

		if (result?.error) {
			setDialogError(result.message || 'Something went wrong');
			return;
		}

		if (folderDialog.mode === 'create' && result?.folder?._id) {
			onSelect(result.folder._id);
		}
		closeFolderDialog();
	};

	const confirmDeleteFolder = async () => {
		if (!deletingFolder) return;

		const result = await dispatch(
			removeTemplateFolder({
				_folder: deletingFolder._id,
				isEnterpriseRequested
			})
		);

		if (result?.error) {
			toast.error(result.message || 'Could not delete the folder');
		} else if (activeFilter === deletingFolder._id) {
			onSelect('all');
		}
		setDeletingFolder(null);
	};

	// Folders are shared containers, not kind-specific: the same list shows in
	// every view so a folder never appears or disappears based on which
	// composer opened the picker. Counts reflect the current kind, so a folder
	// of SMS templates simply reads 0 in the group-chat view.
	const showUnfiled = folders.length > 0 && counts.unfiled > 0;

	const options = React.useMemo(() => {
		const list = [
			{ key: 'all', label: 'All', count: counts.all },
			{ key: 'favorites', label: 'Favorites', count: counts.favorites }
		];

		folders.forEach((folder) => {
			list.push({
				key: folder._id,
				label: folder.title,
				count: counts.byFolder[folder._id] || 0
			});
		});

		if (folders.length > 0 && counts.unfiled > 0) {
			list.push({
				key: 'unfiled',
				label: 'No folder',
				count: counts.unfiled
			});
		}

		return list;
	}, [folders, counts]);

	const activeFolder = folders.find((folder) => folder._id === activeFilter);

	const renderFolderMenu = (folder) => (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					aria-label={`Folder options for ${folder.title}`}
					className={cn(
						'flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground',
						'hover:bg-accent hover:text-foreground',
						'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity',
						'touch:size-8 touch:opacity-100'
					)}
				>
					<Ellipsis className="h-3.5 w-3.5" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start">
				<DropdownMenuItem onClick={() => openRenameDialog(folder)}>
					Rename
				</DropdownMenuItem>
				<DropdownMenuItem
					className="text-destructive focus:text-destructive"
					onClick={() => setDeletingFolder(folder)}
				>
					Delete
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);

	return (
		<>
			{/* Phones: chips that scroll sideways instead of a rail that would crowd the list */}
			<ChipRow
				aria-label="Template folders"
				className={cn(
					'-mx-1 px-1 py-1.5 md:hidden',
					chipsOnShortScreens && 'max-xl:[@media(max-height:560px)]:!flex',
					chipsBelowWide && 'md:max-xl:!flex'
				)}
			>
				{leadingChips}
				{options.map((option) => (
					<Chip
						key={option.key}
						active={activeFilter === option.key}
						icon={option.key === 'favorites' ? Star : undefined}
						count={option.count}
						onClick={() => onSelect(option.key)}
					>
						{option.label}
					</Chip>
				))}
				{canManage && activeFolder && (
					<Chip
						aria-label={`Folder options for ${activeFolder.title}`}
						onClick={() => setFolderActionsOpen(true)}
						className="px-2.5"
					>
						<Ellipsis className="size-4" aria-hidden />
					</Chip>
				)}
				{canManage && (
					<Chip icon={Plus} onClick={openCreateDialog}>
						New folder
					</Chip>
				)}
			</ChipRow>
			{canManage && activeFolder && (
				<ActionSheet
					open={folderActionsOpen}
					onOpenChange={setFolderActionsOpen}
					title={activeFolder.title}
					items={[
						{
							id: 'rename',
							label: 'Rename folder',
							icon: Pencil,
							onSelect: () => openRenameDialog(activeFolder)
						},
						{
							id: 'delete',
							label: 'Delete folder',
							icon: Trash2,
							destructive: true,
							onSelect: () => setDeletingFolder(activeFolder)
						}
					]}
				/>
			)}

			<aside
				className={cn(
					'hidden shrink-0 flex-col md:flex md:w-40 md:border-r md:pr-2 lg:w-48',
					chipsOnShortScreens && 'max-xl:[@media(max-height:560px)]:!hidden',
					chipsBelowWide && 'md:max-xl:!hidden'
				)}
			>
				<div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
					<FolderRow
						label="All"
						count={counts.all}
						active={activeFilter === 'all'}
						onSelect={() => onSelect('all')}
					/>
					<FolderRow
						label="Favorites"
						icon={Star}
						count={counts.favorites}
						active={activeFilter === 'favorites'}
						onSelect={() => onSelect('favorites')}
					/>

					{folders.length > 0 && (
						<p className="px-2 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
							Folders
						</p>
					)}

					{folders.map((folder) => (
						<FolderRow
							key={folder._id}
							label={folder.title}
							count={counts.byFolder[folder._id] || 0}
							active={activeFilter === folder._id}
							menu={canManage ? renderFolderMenu(folder) : null}
							onSelect={() => onSelect(folder._id)}
						/>
					))}

					{showUnfiled && (
						<FolderRow
							label="No folder"
							count={counts.unfiled}
							active={activeFilter === 'unfiled'}
							onSelect={() => onSelect('unfiled')}
						/>
					)}
				</div>

				{canManage && (
					<Button
						variant="ghost"
						size="sm"
						onClick={openCreateDialog}
						className="mt-1 w-full shrink-0 justify-start gap-2 text-muted-foreground hover:text-foreground"
					>
						<Plus className="h-4 w-4" />
						New folder
					</Button>
				)}
			</aside>

			<Dialog
				open={Boolean(folderDialog)}
				onOpenChange={(open) => !open && closeFolderDialog()}
			>
				<DialogContent mobileVariant="dialog">
					<DialogHeader>
						<DialogTitle>
							{folderDialog?.mode === 'rename'
								? 'Rename folder'
								: 'Create a folder'}
						</DialogTitle>
					</DialogHeader>
					<Input
						placeholder="Folder name"
						value={folderTitle}
						maxLength={60}
						autoFocus
						autoComplete="off"
						enterKeyHint="done"
						onChange={(e) => setFolderTitle(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								submitFolderDialog();
							}
						}}
					/>
					{dialogError && (
						<p className="text-sm text-destructive">{dialogError}</p>
					)}
					<DialogFooter>
						<Button variant="ghost" onClick={closeFolderDialog}>
							Cancel
						</Button>
						<Button onClick={submitFolderDialog}>
							{folderDialog?.mode === 'rename'
								? 'Rename'
								: 'Create'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={Boolean(deletingFolder)}
				onOpenChange={(open) => !open && setDeletingFolder(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Delete "{deletingFolder?.title}"?
						</AlertDialogTitle>
						<AlertDialogDescription>
							The templates themselves aren't deleted — they'll
							just move out of this folder.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={confirmDeleteFolder}
						>
							Delete folder
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};

export default FolderFilter;
